import { useState } from "react";

import { OrganizationPageOutput } from "@/common/types/trpc";
import { trpc } from "@/common/helpers/trpc";

export const EVENT_ROWS_STEP = 6;

export type EventsTab = "upcoming" | "past";
export type EventsTabPage = OrganizationPageOutput["upcomingEvents"];
export type EventRow = EventsTabPage["rows"][number];
export type EventRowEvent = EventsTabPage["events"][number];

export const useEventRowsTab = ({
  organizationId,
  tab,
  firstPage,
}: {
  organizationId: string;
  tab: EventsTab;
  firstPage: EventsTabPage;
}) => {
  const [visibleCount, setVisibleCount] = useState(EVENT_ROWS_STEP);
  const [isNextPagesQueryEnabled, setIsNextPagesQueryEnabled] = useState(false);

  const nextPagesQuery = trpc.getOrganizationPageEventRows.useInfiniteQuery(
    { organizationId, tab },
    {
      enabled: isNextPagesQueryEnabled && Boolean(firstPage.nextCursor),
      initialCursor: firstPage.nextCursor ?? undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }
  );

  const nextPages = nextPagesQuery.data?.pages ?? [];
  const rows = [...firstPage.rows, ...nextPages.flatMap((page) => page.rows)];
  const eventsById = new Map<string, EventRowEvent>(
    [...firstPage.events, ...nextPages.flatMap((page) => page.events)].map((event) => [event.id, event])
  );
  const hasMoreRowsOnServer = nextPages.length === 0 ? Boolean(firstPage.nextCursor) : nextPagesQuery.hasNextPage;
  const visibleRows = rows.slice(0, visibleCount);

  const showMoreRows = () => {
    const nextVisibleCount = visibleCount + EVENT_ROWS_STEP;
    setVisibleCount(nextVisibleCount);

    const needsMoreRows = nextVisibleCount + EVENT_ROWS_STEP > rows.length;
    if (!needsMoreRows || !hasMoreRowsOnServer) {
      return;
    }
    if (!isNextPagesQueryEnabled) {
      setIsNextPagesQueryEnabled(true);
      return;
    }
    if (!nextPagesQuery.isFetchingNextPage) {
      void nextPagesQuery.fetchNextPage();
    }
  };

  return {
    visibleRows,
    eventsById,
    totalCount: firstPage.totalCount,
    hasMoreRows: visibleCount < rows.length || hasMoreRowsOnServer,
    isLoadingMoreRows: visibleCount > rows.length && nextPagesQuery.isFetching,
    showMoreRows,
  };
};
